import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Sparkles } from "lucide-react";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public Kit Gallery — social proof / portfolio. Pulls anonymised thumbnails
 * from `/api/gallery` (last 30 days of delivered Kit images, max 4 per order
 * to avoid one customer dominating). Zero PII: no cafe names, emails, or
 * order ids ever exposed client-side.
 */
export default function Gallery() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/gallery?days=30&limit=60`)
            .then(({ data }) => {
                if (!cancelled) setItems(data || []);
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="min-h-screen bg-cafe-cream">
            <SiteNav />
            <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-20">
                {/* Header */}
                <header className="mb-10 sm:mb-14">
                    <Link
                        to="/"
                        data-testid="gallery-back-home"
                        className="inline-flex items-center gap-1.5 text-cafe-mocha hover:text-cafe-espresso text-xs mb-6 transition-colors"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back home
                    </Link>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-cafe-olive font-semibold mb-3">
                        Last 30 days · {items.length} hand-reviewed images
                    </p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cafe-espresso leading-[1.05] mb-3">
                        Recently <em className="font-display italic text-cafe-olive">delivered</em> Kits.
                    </h1>
                    <p className="text-cafe-mocha text-sm sm:text-base max-w-2xl leading-relaxed">
                        A peek at the work — anonymised. Each thumbnail below comes from a real café's 30-day content kit, custom-rendered for their brand and hand-reviewed by Kaily before delivery. No two Kits look the same.
                    </p>
                </header>

                {/* Grid */}
                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="gallery-loading">
                        {Array.from({ length: 15 }).map((_, i) => (
                            <div
                                key={i}
                                className="aspect-square rounded-xl bg-cafe-latte/40 animate-pulse"
                                style={{ animationDelay: `${i * 50}ms` }}
                            />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div
                        className="text-center py-20 border border-dashed border-border rounded-2xl"
                        data-testid="gallery-empty"
                    >
                        <Sparkles className="h-8 w-8 text-cafe-olive mx-auto mb-3" />
                        <p className="font-serif text-2xl text-cafe-espresso mb-2">No deliveries yet in the last 30 days.</p>
                        <p className="text-cafe-mocha text-sm max-w-md mx-auto">
                            Once Kits start shipping out, you'll see anonymised thumbnails here as social proof for new customers.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="gallery-grid">
                        {items.map((it, i) => (
                            <GalleryCard key={`${it.url}-${i}`} item={it} index={i} />
                        ))}
                    </div>
                )}

                {/* CTA */}
                <div className="mt-16 border-t border-border pt-10 text-center">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-cafe-olive font-semibold mb-2">Your café, next</p>
                    <h2 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-3">
                        Want one of these?
                    </h2>
                    <p className="text-cafe-mocha text-sm max-w-md mx-auto mb-6 leading-relaxed">
                        Pick a kit · submit your brand brief · receive 30 days of IG content trained on your real product photos.
                    </p>
                    <Link
                        to="/#pricing"
                        data-testid="gallery-cta-pricing"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream text-xs font-semibold uppercase tracking-[0.18em] transition-colors"
                    >
                        See pricing
                    </Link>
                </div>
            </main>
            <SiteFooter />
        </div>
    );
}

/** Single tile in the grid — square thumbnail with a soft vibe overlay on hover. */
function GalleryCard({ item, index }) {
    return (
        <div
            className="group relative aspect-square rounded-xl overflow-hidden bg-cafe-latte/40 border border-border"
            style={{ animation: `fadeInUp 400ms ${index * 30}ms both` }}
        >
            <img
                src={item.url}
                alt={`${item.cafe_type} · ${item.vibe || ""}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {/* Vibe overlay — appears on hover, shows generic cafe_type + one_word vibe.
                NEVER shows the cafe name (PII protection). */}
            <div className="absolute inset-0 bg-gradient-to-t from-cafe-espresso/70 via-cafe-espresso/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                <div>
                    <p className="text-cafe-cream text-[10px] uppercase tracking-[0.2em] font-semibold">
                        {item.cafe_type}
                    </p>
                    {item.vibe && (
                        <p className="text-cafe-cream/85 text-xs italic font-display">"{item.vibe}"</p>
                    )}
                </div>
            </div>
        </div>
    );
}
